import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import Group from 'components/utils/Group'
import DualSlider from 'LuxKit/Slider/DualSlider'
import useCruisePriceByNight from 'hooks/useCruisePriceByNight'
import type { ButtonToggleItem } from 'components/Common/ButtonGroupToggle/ButtonGroupToggleItem'
import useQueryParams from 'hooks/useQueryParams'
import debounce from 'lodash/debounce'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import styled from 'styled-components'
import ButtonGroupToggle from 'components/Common/ButtonGroupToggle/ButtonGroupToggle'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import { useCurrencyFormatter } from 'hooks/useNumberFormatters'
import TextInputOnly from 'components/Common/Form/Input/TextInputOnly'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { clamp } from 'lib/maths/mathUtils'

const TabGroupToggle = styled(ButtonGroupToggle)`
  width: 100%;

  ${mediaQueryUp.tablet} {
    width: ${rem(340)};
  }

  &.drawer {
    width: ${rem(300)};
  }

  > button {
    min-width: auto !important;
  }
`

const PriceInputsContainer = styled(Group)`
  gap: ${rem(8)};
`

const CurrencyInput = styled(TextInputOnly)`
  flex: 1;
  min-width: 0;
`

const InputWithError = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  gap: ${rem(4)};
`

const ErrorMessage = styled.span`
  display: block;
  font-size: ${rem(12)};
  line-height: 1.25;
  color: ${props => props.theme.palette.messaging.critical.normalForeground};
  min-height: 0;
`

const SliderContainer = styled(VerticalSpacer)`
  position: relative;
  width: 100%;
`

function parseCurrencyInput(inputValue: string): number {
  const digits = inputValue.replace(/\D/g, '')
  return digits ? parseInt(digits, 10) : 0
}

export interface CruisePriceFilterProps {
  minPrice?: number
  maxPrice?: number
  price?: {
    min?: number
    max?: number
  }
}

export interface CruisePriceFilterChangeProps {
  priceBy?: string
  minPrice?: number
  maxPrice?: number
}

interface Props {
  filters: CruisePriceFilterProps
  onChange: (nextFilters: CruisePriceFilterChangeProps) => void
  facets: Array<App.CruiseSearchFacet>
  fetching?: boolean
}

const SLIDER_DEBOUNCE_MS = 500
const INPUT_DEBOUNCE_MS = 2000

function CruisePriceFilter({
  filters,
  onChange,
  facets,
  fetching,
}: Props) {
  const intl = useIntl()
  const containerRef = useRef<HTMLDivElement | null>(null)
  const queryParams = useQueryParams()
  const cruisePriceByNight = useCruisePriceByNight()
  const formatCurrency = useCurrencyFormatter()

  const tabButtons: Array<ButtonToggleItem> = useMemo(() => [
    {
      id: 'fullprice',
      label: intl.formatMessage({
        id: 'Nlwo6ic+OB',
        defaultMessage: 'Full price',
        description: 'Tab label for full price display mode in cruise price filter',
      }),
    },
    {
      id: 'night',
      label: intl.formatMessage({
        id: '6BVLQkp0yY',
        defaultMessage: 'Per night',
        description: 'Tab label for per-night price display mode in cruise price filter',
      }),
    },
  ], [intl])

  const defaultTab = !queryParams?.get('priceBy') || !cruisePriceByNight ? tabButtons[0] : tabButtons[1]
  const [tab, setTab] = useState<ButtonToggleItem>(defaultTab)

  const filterPrice = useMemo(() => {
    const min = filters.price?.min! ?? filters.minPrice
    const max = filters.price?.max! ?? filters.maxPrice
    return { min, max }
  }, [filters])

  const priceFacet = useMemo(() => {
    const facet = facets.find(facet => facet.category === 'cruise_prices')
    const min = cruisePriceByNight ? facet?.minPerNight : facet?.min
    const max = cruisePriceByNight ? facet?.maxPerNight : facet?.max
    return {
      min: min ?? 0,
      max: Math.ceil(max ?? 0),
    }
  }, [cruisePriceByNight, facets])

  const facetMin = Math.ceil(priceFacet?.min ?? 0)
  const facetMax = Math.ceil(priceFacet?.max ?? 0)
  const effectiveMin = filterPrice.min ?? facetMin
  const effectiveMax = filterPrice.max ?? facetMax

  const [localMin, setLocalMin] = useState<number>(effectiveMin)
  const [localMax, setLocalMax] = useState<number>(effectiveMax)

  useEffect(() => {
    setLocalMin(effectiveMin)
    setLocalMax(effectiveMax)
  }, [effectiveMin, effectiveMax])

  const minError = useMemo(() => {
    if (localMin < facetMin) {
      return intl.formatMessage(
        { id: 'bZdD+XIocz', defaultMessage: 'Minimum is {min}', description: 'Cruise price filter min out of range in the cruise offer or booking section' },
        { min: formatCurrency(facetMin) ?? `A$${facetMin}` },
      )
    }
    if (localMin > localMax) {
      return intl.formatMessage({
        id: 'nZb5a4BYJv',
        defaultMessage: 'Minimum must be less than maximum',
        description: 'Cruise price filter min greater than max in the cruise section',
      })
    }
    return undefined
  }, [intl, formatCurrency, facetMin, localMin, localMax])

  const maxError = useMemo(() => {
    if (localMax > facetMax) {
      return intl.formatMessage(
        { id: 'zfEowFdRTt', defaultMessage: 'Maximum is {max}', description: 'Cruise price filter max out of range in the cruise offer or booking section' },
        { max: formatCurrency(facetMax) ?? `A$${facetMax}` },
      )
    }
    if (localMax < localMin) {
      return intl.formatMessage({
        id: 'PG6ZgO8Zoi',
        defaultMessage: 'Maximum must be greater than minimum',
        description: 'Cruise price filter max less than min in the cruise offer or booking section',
      })
    }
    return undefined
  }, [intl, formatCurrency, facetMax, localMin, localMax])

  // set tab based on query params
  useEffect(() => {
    setTab(!queryParams?.get('priceBy') || !cruisePriceByNight ? tabButtons[0] : tabButtons[1])
  }, [queryParams, cruisePriceByNight, tabButtons])

  const onTabSelect = useCallback((nextTab: ButtonToggleItem) => {
    setTab(nextTab)
    onChange({ priceBy: nextTab.id, minPrice: undefined, maxPrice: undefined })
  }, [onChange])

  const onDelayedMinPriceChange = useMemo(() => debounce((e: { target: { value?: string } }) => {
    onChange({ minPrice: Number(e.target.value ?? 0) })
  }, SLIDER_DEBOUNCE_MS), [onChange])

  const onDelayedMaxPriceChange = useMemo(() => debounce((e: { target: { value?: string } }) => {
    onChange({ maxPrice: Number(e.target.value ?? 0) })
  }, SLIDER_DEBOUNCE_MS), [onChange])

  const onSliderMinChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.valueAsNumber
    setLocalMin(value)
    if (value >= facetMin && value <= facetMax && value <= localMax) {
      onDelayedMinPriceChange(e)
    }
  }, [onDelayedMinPriceChange, facetMin, facetMax, localMax])

  const onSliderMaxChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.valueAsNumber
    setLocalMax(value)
    if (value >= facetMin && value <= facetMax && value >= localMin) {
      onDelayedMaxPriceChange(e)
    }
  }, [onDelayedMaxPriceChange, facetMin, facetMax, localMin])

  const onInputMinCommit = useMemo(() => debounce((value: number) => {
    onChange({ minPrice: value })
  }, INPUT_DEBOUNCE_MS), [onChange])

  const onInputMaxCommit = useMemo(() => debounce((value: number) => {
    onChange({ maxPrice: value })
  }, INPUT_DEBOUNCE_MS), [onChange])

  useEffect(() => {
    return () => {
      onDelayedMinPriceChange.cancel()
      onDelayedMaxPriceChange.cancel()
      onInputMinCommit.cancel()
      onInputMaxCommit.cancel()
    }
  }, [onDelayedMinPriceChange, onDelayedMaxPriceChange, onInputMinCommit, onInputMaxCommit])

  const formattedMin = useMemo(() => formatCurrency(localMin) ?? '', [formatCurrency, localMin])
  const formattedMax = useMemo(() => formatCurrency(localMax) ?? '', [formatCurrency, localMax])

  const onInputMinChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = parseCurrencyInput(e.currentTarget.value)
    const value = Math.round(raw)
    setLocalMin(value)
    if (value >= facetMin && value <= facetMax && value <= localMax) {
      onInputMinCommit(value)
    }
  }, [facetMin, facetMax, localMax, onInputMinCommit])

  const onInputMaxChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = parseCurrencyInput(e.currentTarget.value)
    const value = Math.round(raw)
    setLocalMax(value)
    if (value >= facetMin && value <= facetMax && value >= localMin) {
      onInputMaxCommit(value)
    }
  }, [facetMin, facetMax, localMin, onInputMaxCommit])

  return <Group direction="vertical" gap={16} ref={containerRef}>
    <Group direction="horizontal" horizontalAlign="center" data-testid="cruise-extra-filters-price-tab">
      <TabGroupToggle
        items={tabButtons}
        activeItem={tab}
        onItemClick={onTabSelect}
      />
    </Group>
    {fetching && <TextLoadingBox width="100%" />}
    {!fetching && <>
      <SliderContainer gap={4}>
        <DualSlider
          data-testid="cruise-extra-filters-price-slider"
          key={`${facetMin}-${facetMax}`}
          min={facetMin}
          max={facetMax}
          startValue={clamp(facetMin, localMin, Math.min(localMax, facetMax))}
          endValue={clamp(Math.max(localMin, facetMin), localMax, facetMax)}
          onStartChange={onSliderMinChange}
          onEndChange={onSliderMaxChange}
          name="price"
        />
      </SliderContainer>
      <PriceInputsContainer direction="horizontal" horizontalAlign="space-between" fullWidth verticalAlign="start">
        <InputWithError>
          <CurrencyInput
            data-testid="cruise-extra-filters-price-min-input"
            type="text"
            inputMode="numeric"
            value={formattedMin}
            onChange={onInputMinChange}
            noValidationSpacing
            noValidationMessage
            aria-label={intl.formatMessage({
              id: 'nfqV75igKZ',
              defaultMessage: 'Minimum price',
              description: 'Accessibility label for minimum price input in cruise price filter',
            })}
            manualError={!!minError}
            invalidErrorMessage={minError}
          />
          {minError && <ErrorMessage role="alert">{minError}</ErrorMessage>}
        </InputWithError>
        <InputWithError>
          <CurrencyInput
            data-testid="cruise-extra-filters-price-max-input"
            type="text"
            inputMode="numeric"
            value={formattedMax}
            onChange={onInputMaxChange}
            noValidationSpacing
            noValidationMessage
            aria-label={intl.formatMessage({
              id: '2Q2YgsL4H5',
              defaultMessage: 'Maximum price',
              description: 'Accessibility label for maximum price input in cruise price filter',
            })}
            manualError={!!maxError}
            invalidErrorMessage={maxError}
          />
          {maxError && <ErrorMessage role="alert">{maxError}</ErrorMessage>}
        </InputWithError>
      </PriceInputsContainer>
    </>}
  </Group>
}

export default CruisePriceFilter
